"use client";

import { useState } from "react";
import Link from "next/link";
import { Smartphone, CheckCircle, AlertCircle } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { AppPageShell } from "@/components/layout/AppPageShell";
import {
  callDanalPass,
  loadDanalScript,
  type DanalPassResult,
} from "@/lib/auth/danal-pass-api";
import toast from "react-hot-toast";

export default function IdentityVerificationPage() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DanalPassResult | null>(null);
  const [agreed, setAgreed] = useState(false);

  const handleVerify = async () => {
    if (!agreed) {
      toast.error("이용약관 및 개인정보취급방침에 동의해 주세요.");
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      await loadDanalScript();
      const res = await callDanalPass();
      setResult(res);
      if (res.success) {
        toast.success("본인인증이 완료되었습니다.");
      } else {
        toast.error(res.error || "본인인증에 실패했습니다.");
      }
    } catch {
      toast.error("본인인증 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppPageShell
      title="휴대폰 본인인증"
      description="ConnecTone 서비스 이용을 위해 본인 확인이 필요합니다. 통신사 PASS 또는 SMS 인증을 통해 본인을 확인합니다."
      maxWidth="md"
    >
        <Card className="p-6 space-y-6 border border-gray-200 shadow-sm">
          <div className="rounded-lg bg-gray-50 p-4 text-sm text-gray-600 leading-relaxed">
            <p className="font-medium text-gray-800 mb-2">수집·이용 목적</p>
            <ul className="list-disc list-inside space-y-1">
              <li>회원 본인 확인 및 중복 가입 방지</li>
              <li>미성년자 이용 제한 및 서비스 부정 이용 방지</li>
              <li>결제·정산 시 본인 확인</li>
            </ul>
          </div>

          <label className="flex items-start gap-3 cursor-pointer text-sm text-gray-700">
            <input
              type="checkbox"
              checked={agreed}
              onChange={e => setAgreed(e.target.checked)}
              className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span>
              <Link href="/terms" className="text-violet-600 hover:underline">
                이용약관
              </Link>
              {" 및 "}
              <Link href="/privacy" className="text-violet-600 hover:underline">
                개인정보취급방침
              </Link>
              에 동의합니다. (필수)
            </span>
          </label>

          <Button
            variant="primary"
            size="lg"
            className="w-full"
            disabled={loading || !agreed}
            onClick={handleVerify}
          >
            {loading ? (
              "인증 진행 중..."
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Smartphone className="w-5 h-5" />
                휴대폰 본인인증 시작
              </span>
            )}
          </Button>

          {result && (
            <div
              className={`flex items-start gap-3 rounded-lg p-4 text-sm ${
                result.success
                  ? "bg-green-50 text-green-800 border border-green-200"
                  : "bg-red-50 text-red-800 border border-red-200"
              }`}
            >
              {result.success ? (
                <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              )}
              <div>
                {result.success ? (
                  <>
                    <p className="font-medium">본인인증 완료</p>
                    <p className="mt-1 text-green-700">
                      {result.name} · {result.phone?.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3")}
                    </p>
                  </>
                ) : (
                  <p>{result.error || "인증에 실패했습니다."}</p>
                )}
              </div>
            </div>
          )}
        </Card>

        <p className="text-center text-xs text-gray-500 mt-6">
          본인인증 서비스는 다날(Danal) 등 공인된 인증 기관을 통해 제공됩니다.
        </p>
    </AppPageShell>
  );
}
